import React, { createContext, useContext, useState, useEffect } from 'react';
import {
    signOut,
    onAuthStateChanged,
    sendPasswordResetEmail,
    signInWithCustomToken
} from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db, functions } from '../firebase';
import { httpsCallable } from 'firebase/functions';

const AuthContext = createContext();

export function useAuth() {
    return useContext(AuthContext);
}

export function AuthProvider({ children }) {
    const [currentUser, setCurrentUser] = useState(null);
    const [userRole, setUserRole] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [merchantData, setMerchantData] = useState(null);
    const [selectedHierarchy, setSelectedHierarchy] = useState(null);

    // Login with merchant ID and password (via custom token)
    async function login(emailOrMerchantId, password) {
        setError('');
        try {
            if (!emailOrMerchantId || !password) {
                throw new Error('Please enter both Merchant ID/email and password.');
            }

            const merchantLoginFn = httpsCallable(functions, 'merchantLogin');
            const response = await merchantLoginFn({ merchantId: emailOrMerchantId, password });
            const { token, merchant } = response.data;

            console.log('token:', token);
            console.log('merchant:', merchant);

            if (!token || !merchant) {
                throw new Error('Invalid credentials or merchant not found.');
            }

            // Sign in with custom token
            await signInWithCustomToken(auth, token);

            // Set merchant data in state
            setMerchantData(merchant);
            setCurrentUser({
                uid: merchant.id,
                email: merchant.email,
                displayName: merchant.name
            });
            setUserRole('admin');
            localStorage.setItem('merchantId', merchant.id);

            return { success: true };
        } catch (err) {
            console.error('Login error:', err);
            setError(err.message);
            return { success: false, error: err.message };
        }
    }

    // Logout
    async function logout() {
        setError('');
        try {
            localStorage.removeItem('merchantId');
            localStorage.removeItem('selectedHierarchy');
            await signOut(auth);
            setCurrentUser(null);
            setUserRole(null);
            setMerchantData(null);
            setSelectedHierarchy(null);
            return { success: true };
        } catch (err) {
            console.error('Logout error:', err);
            setError(err.message);
            return { success: false, error: err.message };
        }
    }

    // Reset password
    async function resetPassword(email) {
        setError('');
        try {
            await sendPasswordResetEmail(auth, email);
            return { success: true };
        } catch (err) {
            setError(err.message);
            return { success: false, error: err.message };
        }
    }

    useEffect(() => {
        // Load saved hierarchy
        const savedHierarchy = localStorage.getItem('selectedHierarchy');
        if (savedHierarchy) {
            try {
                setSelectedHierarchy(JSON.parse(savedHierarchy));
            } catch (e) {
                console.error('Error parsing saved hierarchy:', e);
                localStorage.removeItem('selectedHierarchy');
            }
        }

        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            setLoading(true);
            if (user) {
                try {
                    const merchantId = localStorage.getItem('merchantId');
                    if (merchantId) {
                        const merchantDoc = await getDoc(doc(db, 'merchants', merchantId));
                        if (merchantDoc.exists()) {
                            setMerchantData({ id: merchantDoc.id, ...merchantDoc.data() });
                            setUserRole('admin');
                            setCurrentUser(user);
                        } else {
                            console.warn('Merchant document not found, signing out');
                            await logout();
                        }
                    } else {
                        // Optionally handle employee/admin users here
                        setCurrentUser(user);
                        setUserRole(null);
                        setMerchantData(null);
                    }
                } catch (err) {
                    console.error('Error during auth state check:', err);
                    await logout();
                }
            } else {
                setCurrentUser(null);
                setUserRole(null);
                setMerchantData(null);
            }
            setLoading(false);
        });

        return unsubscribe;
    }, []);

    const value = {
        currentUser,
        userRole,
        merchantData,
        selectedHierarchy,
        setSelectedHierarchy,
        login,
        logout,
        resetPassword,
        error,
        loading
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
